import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiKey, FiPhone } from "react-icons/fi";
import {
  fundswallaOtpHeaders,
  sendOtp1Url,
  verifyOtpPhpUrl,
} from "../../config/applyLoanOtp";
import { getApiMessage } from "../../config/taskApi";
import { setMobileOtpSecure } from "../../utils/applyFlowStorage";
import {
  APPLY_LOAN_MOBILE_OTP_RESEND_SECONDS,
  clearApplyLoanMobileOtpDraft,
  getApplyLoanMobileOtpDraft,
  saveApplyLoanMobileOtpDraft,
} from "../../utils/applyLoanStorage";
import "./AadhaarInput.css";

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const OTP_RESEND_SECONDS = APPLY_LOAN_MOBILE_OTP_RESEND_SECONDS;

const IconField = ({ icon: Icon, children, className = "" }) => (
  <div className={`loan-icon-field ${className}`.trim()}>
    <span className="loan-icon-field__icon" aria-hidden="true">
      <Icon />
    </span>
    {children}
  </div>
);

const readInitialOtpDraft = () => {
  const draft = getApplyLoanMobileOtpDraft();
  if (!draft) {
    return {
      mobile: "",
      consent: false,
      otpSent: false,
      retryTimer: 0,
    };
  }
  return {
    mobile: draft.mobile,
    consent: true,
    otpSent: true,
    retryTimer: draft.retryTimer,
  };
};

const MobileOtpStep1 = ({ onNext, hideHero = false, skipOtp = false }) => {
  const initial = readInitialOtpDraft();
  const [mobile, setMobile] = useState(initial.mobile);
  const [consent, setConsent] = useState(initial.consent);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(skipOtp ? false : initial.otpSent);
  const [otpError, setOtpError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [retryTimer, setRetryTimer] = useState(initial.retryTimer);
  const sendLockRef = useRef(false);
  const verifyLockRef = useRef(false);

  const isMobileValid = MOBILE_REGEX.test(mobile);
  const isFormValid = isMobileValid && consent;
  const isOtpValid = /^\d{6}$/.test(otp);

  useEffect(() => {
    if (retryTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setRetryTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [retryTimer]);

  const markOtpSentLocally = (mobileValue) => {
    const sentAt = Date.now();
    saveApplyLoanMobileOtpDraft({ mobile: mobileValue, sentAt });
    setOtpSent(true);
    setOtp("");
    setConsent(true);
    setMobileError("");
    setRetryTimer(OTP_RESEND_SECONDS);
  };

  const sendOtp = async () => {
    // First send needs consent; Resend only needs a valid mobile
    if (!otpSent && !isFormValid) return;
    if (otpSent && !isMobileValid) return;
    if (sendLockRef.current) return;
    sendLockRef.current = true;

    setLoading(true);
    setMobileError("");
    setOtpError("");

    try {
      const params = new URLSearchParams();
      params.append("mobile", mobile);

      const res = await axios.post(sendOtp1Url(), params, {
        headers: fundswallaOtpHeaders(),
        timeout: 20000,
        validateStatus: () => true,
      });

      const data = res.data || {};
      const apiMessage = getApiMessage(data);
      const lower = apiMessage.toLowerCase();
      const apiReturn = data?.api_response?.return;
      const topStatus = data?.status;

      if (
        res.status >= 400 ||
        apiReturn === false ||
        topStatus === false ||
        lower.includes("invalid")
      ) {
        if (!otpSent) setOtpSent(false);
        setMobileError(apiMessage || "Unable to send OTP. Please try again.");
        return;
      }

      const otpSentOk =
        apiReturn === true ||
        topStatus === true ||
        lower.includes("otp sent") ||
        lower.includes("sent successfully") ||
        Boolean(data?.data);

      if (!otpSentOk && res.status !== 200) {
        if (!otpSent) setOtpSent(false);
        setMobileError(apiMessage || "Unable to send OTP. Please try again.");
        return;
      }

      if (
        otpSentOk ||
        (res.status === 200 &&
          !lower.includes("fail") &&
          !lower.includes("error") &&
          !lower.includes("exceed"))
      ) {
        markOtpSentLocally(mobile);
        return;
      }

      if (!otpSent) setOtpSent(false);
      setMobileError(apiMessage || "Unable to send OTP. Please try again.");
    } catch {
      if (!otpSent) setOtpSent(false);
      setMobileError("Could not reach OTP service. Please try again.");
    } finally {
      sendLockRef.current = false;
      setLoading(false);
    }
  };

  const resetToMobileEntry = () => {
    clearApplyLoanMobileOtpDraft();
    setOtpSent(false);
    setOtp("");
    setConsent(false);
    setRetryTimer(0);
    setMobileError("");
    setOtpError("");
  };

  const continueWithoutOtp = () => {
    if (!isFormValid) return;
    clearApplyLoanMobileOtpDraft();
    localStorage.setItem("mobile_verified", "true");
    localStorage.setItem("mobileNumber", mobile);

    const existing = localStorage.getItem("loanFormData");
    let loan = {};
    try {
      loan = existing ? JSON.parse(existing) : {};
    } catch {
      loan = {};
    }
    localStorage.setItem(
      "loanFormData",
      JSON.stringify({
        ...loan,
        mobile,
        mobile_verified: true,
      })
    );

    onNext?.(mobile);
  };

  const verifyOtp = async () => {
    if (!isOtpValid) {
      setOtpError("Enter the 6-digit OTP");
      return;
    }
    if (verifyLockRef.current) return;
    verifyLockRef.current = true;

    setOtpLoading(true);
    setOtpError("");

    try {
      const params = new URLSearchParams();
      params.append("mobile", mobile);
      params.append("otp", otp);

      const res = await axios.post(verifyOtpPhpUrl(), params, {
        headers: fundswallaOtpHeaders(),
        timeout: 20000,
        validateStatus: () => true,
      });

      const data = res.data || {};
      const apiMessage = getApiMessage(data);
      const ok =
        data?.status === true ||
        data?.success === true ||
        data?.Status === 1 ||
        String(data?.status).toLowerCase() === "success";

      if (!ok) {
        setOtpError(apiMessage || "Entered OTP is incorrect");
        return;
      }

      clearApplyLoanMobileOtpDraft();
      localStorage.setItem("mobile_verified", "true");
      localStorage.setItem("mobileNumber", mobile);
      setMobileOtpSecure(otp);

      const existing = localStorage.getItem("loanFormData");
      let loan = {};
      try {
        loan = existing ? JSON.parse(existing) : {};
      } catch {
        loan = {};
      }
      localStorage.setItem(
        "loanFormData",
        JSON.stringify({
          ...loan,
          mobile,
          mobile_verified: true,
          otp: String(otp || "").trim(),
        })
      );

      onNext?.(mobile);
    } catch {
      setOtpError("OTP verification failed. Please try again.");
    } finally {
      verifyLockRef.current = false;
      setOtpLoading(false);
    }
  };

  return (
    <div className="apply-now1-focus apply-loan-mobile-focus">
      <div className="apply-now1-focus-card">
        {!hideHero && (
        <div className="apply-form-head">
          <div className="apply-now1-hero">
            <span className="apply-now1-hero__eyebrow">Start Application</span>
              <h2>{skipOtp ? "Enter Mobile Number" : "Verify Mobile Number"}</h2>
              <p>
                {skipOtp
                  ? "Enter your mobile number to begin."
                  : "Enter your mobile number to begin and confirm the OTP below."}
              </p>
          </div>
        </div>
        )}

        <div className="apply-field">
          <label>
            Mobile Number <span className="req">*</span>
          </label>
          <IconField icon={FiPhone}>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={mobile}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, "").slice(0, 10);
                setMobile(next);
                setMobileError("");
              }}
              placeholder="10-digit mobile"
              disabled={!skipOtp && otpSent}
              className={`apply-flow-input ${isMobileValid ? "valid" : ""} ${
                mobileError && !mobileError.toLowerCase().includes("sent") ? "error" : ""
              }`}
            />
          </IconField>
          {mobileError && (
            <div
              className={
                mobileError.toLowerCase().includes("sent")
                  ? "apply-flow-success"
                  : "apply-flow-error"
              }
            >
              {mobileError}
            </div>
          )}
        </div>

        {!otpSent && (
          <div className="apply-flow-consent">
            <input
              type="checkbox"
              checked={consent}
              onChange={() => setConsent(!consent)}
            />
            <label>
              I authorize <b>mobile verification</b> and <b>digital communications</b>.
            </label>
          </div>
        )}

        {!otpSent && (
          <div className="apply-flow-actions apply-flow-actions--inline">
            <button
              type="button"
              className="apply-flow-btn apply-flow-btn--primary"
              disabled={!isFormValid || loading}
              onClick={skipOtp ? continueWithoutOtp : sendOtp}
            >
              {skipOtp ? "Continue" : loading ? "Sending..." : "Get OTP"}
            </button>
          </div>
        )}

        {!skipOtp && otpSent && (
          <div className="apply-now1-otp-panel">
            <p className="apply-flow-success">OTP sent successfully</p>
            <div className="apply-field">
              <label>
                OTP <span className="req">*</span>
              </label>
              <IconField icon={FiKey}>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  placeholder="6-digit OTP"
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) {
                      setOtp(e.target.value);
                      setOtpError("");
                    }
                  }}
                  className={`apply-flow-input ${isOtpValid ? "valid" : ""} ${
                    otpError ? "error" : ""
                  }`}
                />
              </IconField>
              {otpError && <div className="apply-flow-error">{otpError}</div>}
            </div>

            <p className="apply-now1-entry__resend">
              {retryTimer > 0 ? (
                <span>Resend in {retryTimer}s</span>
              ) : (
                <button
                  type="button"
                  className="apply-now1-entry__link"
                  onClick={sendOtp}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Resend OTP"}
                </button>
              )}
            </p>

            <div className="apply-flow-actions apply-flow-actions--inline">
              <button
                type="button"
                className="apply-flow-btn apply-flow-btn--secondary"
                onClick={resetToMobileEntry}
                disabled={otpLoading}
              >
                Back
              </button>
              <button
                type="button"
                className="apply-flow-btn apply-flow-btn--primary"
                disabled={!isOtpValid || otpLoading}
                onClick={verifyOtp}
              >
                {otpLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileOtpStep1;
